import { useState } from "react";
import { toast } from "sonner";

// Deep-link launchers for the major chat assistants. The `?q=` patterns
// pre-fill the input box on the destination so the user lands with the
// prompt already pasted in. Gemini doesn't currently honor a query-param
// prefill, so we send the user to the app and rely on clipboard.
const LAUNCHERS = [
  {
    key: "claude",
    label: "claude",
    href: (text) =>
      `https://claude.ai/new?q=${encodeURIComponent(text)}`,
  },
  {
    key: "chatgpt",
    label: "chatgpt",
    href: (text) =>
      `https://chat.openai.com/?q=${encodeURIComponent(text)}`,
  },
  {
    key: "gemini",
    label: "gemini",
    // Gemini doesn't support URL prefill yet — open the app, user pastes.
    href: () => "https://gemini.google.com/app",
  },
  {
    key: "perplexity",
    label: "perplexity",
    href: (text) =>
      `https://www.perplexity.ai/search?q=${encodeURIComponent(text)}`,
  },
];

export default function PromptBlock({ promptText }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      toast.success("prompt copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("clipboard not available");
    }
  };

  const launch = (entry) => async () => {
    // Always copy to clipboard first so the user is one paste away
    // regardless of whether the launcher honors `?q=` prefill.
    try {
      await navigator.clipboard.writeText(promptText);
    } catch {
      // non-fatal; user can still paste manually after we open the launcher
    }
    window.open(entry.href(promptText), "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mt-6 border border-black" data-testid="prompt-block">
      <div className="flex items-center justify-between border-b border-black bg-black text-white px-3 py-1.5 text-[11px] font-bold">
        <span>{">_ prompt body"}</span>
        <button
          type="button"
          onClick={copy}
          className="tv-btn-onblack"
          data-testid="prompt-copy-btn"
        >
          {copied ? "[ copied! ]" : "[ copy ]"}
        </button>
      </div>
      <pre
        className="bg-white text-black text-[12px] leading-[1.55] p-3 whitespace-pre-wrap overflow-x-auto font-mono"
        data-testid="prompt-text"
      >
        {promptText}
      </pre>
      <div className="border-t border-black px-3 py-2 flex flex-wrap items-center gap-2 text-[12px] bg-[#fafafa]">
        <span className="text-[#888]">open in:</span>
        {LAUNCHERS.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={launch(l)}
            className="tv-link"
            data-testid={`prompt-launch-${l.key}`}
          >
            [{l.label} →]
          </button>
        ))}
      </div>
    </section>
  );
}
